import { devError } from '@/lib/logger';
import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, ArrowUp, ArrowDown, Trash2, Shield, Search, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { format, subDays } from 'date-fns';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASON_LABELS = { overtime: 'Hora Extra', early_exit: 'Saída Antecipada' };

// Normalize free-form time input (e.g. "22:30", "22.30", "2230") to "HH:MM" or null
function normalizeTime(value) {
  if (!value) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  const colonMatch = trimmed.match(/^(\d{1,2})[:.,](\d{1,2})$/);
  if (colonMatch) {
    const h = parseInt(colonMatch[1], 10);
    const m = parseInt(colonMatch[2], 10);
    if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    }
    return null;
  }
  const compactMatch = trimmed.match(/^(\d{3,4})$/);
  if (compactMatch) {
    const padded = compactMatch[1].padStart(4, '0');
    const h = parseInt(padded.slice(0, 2), 10);
    const m = parseInt(padded.slice(2), 10);
    if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    }
  }
  return null;
}

function timeToMinutes(value) {
  const norm = normalizeTime(value);
  if (norm === null) return null;
  const [h, m] = norm.split(':').map(Number);
  return h * 60 + m;
}

export default function Adjustments() {
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filterEmployee, setFilterEmployee] = useState('all');
  const [filterLocation, setFilterLocation] = useState('all');
  const [filterReason, setFilterReason] = useState('all');
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(null);
  const [form, setForm] = useState({
    employee_id: '',
    location_id: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    original_time_out: '',
    new_time_out: '',
    reason: 'overtime',
    justification: '',
  });

  useEffect(() => {
    (async () => {
      try {
        const [empRes, locRes] = await Promise.all([
          axios.get(`${API}/employees?status=active`, { withCredentials: true }),
          axios.get(`${API}/locations`, { withCredentials: true }),
        ]);
        setEmployees(empRes.data);
        setLocations(locRes.data);
      } catch (err) { devError('adj-init', err); }
    })();
  }, []);

  const fetchAdjustments = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (filterEmployee !== 'all') params.employee_id = filterEmployee;
      if (filterLocation !== 'all') params.location_id = filterLocation;
      if (filterReason !== 'all') params.reason = filterReason;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await axios.get(`${API}/time-adjustments`, { params, withCredentials: true });
      setItems(res.data.adjustments || []);
    } catch (err) {
      devError('adj-fetch', err);
      toast.error('Erro ao carregar ajustes');
    } finally {
      setLoading(false);
    }
  }, [filterEmployee, filterLocation, filterReason, startDate, endDate]);

  useEffect(() => { fetchAdjustments(); }, [fetchAdjustments]);

  // When the user picks an employee, auto-fill the location with their primary
  // assigned location so they don't have to pick it manually (and don't forget it).
  useEffect(() => {
    if (!dialogOpen || !form.employee_id) return;
    const emp = employees.find((e) => e.employee_id === form.employee_id);
    if (!emp) return;
    const primary = emp.location_id || (Array.isArray(emp.location_ids) ? emp.location_ids[0] : '');
    if (primary && primary !== form.location_id) {
      setForm((prev) => ({ ...prev, location_id: primary }));
    }
  }, [dialogOpen, form.employee_id, employees, form.location_id]);

  // Auto-fill original_time_out from scheduled-end whenever employee+date are set
  // and original is still empty. Helps the gerente skip the math.
  useEffect(() => {
    if (!dialogOpen) return;
    if (!form.employee_id || !form.date) return;
    if (form.original_time_out) return;
    let cancelled = false;
    axios
      .get(`${API}/employees/${form.employee_id}/scheduled-end`, {
        params: { date: form.date },
        withCredentials: true,
      })
      .then((res) => {
        if (cancelled) return;
        if (res.data?.scheduled_end) {
          setForm((prev) => prev.original_time_out ? prev : { ...prev, original_time_out: res.data.scheduled_end });
        }
      })
      .catch((err) => {
        devError('adj-prefill-original', err);
        // best-effort — original stays empty if backend lookup fails
      });
    return () => { cancelled = true; };
  }, [dialogOpen, form.employee_id, form.date, form.original_time_out]);

  const computeDeltaMinutes = () => {
    const o = timeToMinutes(form.original_time_out);
    const n = timeToMinutes(form.new_time_out);
    if (o === null || n === null) return null;
    return n - o;
  };

  const submitAdjustment = async () => {
    if (!form.employee_id) { toast.error('Selecione o funcionário'); return; }
    if (!form.location_id) { toast.error('Selecione o local'); return; }

    const newOut = normalizeTime(form.new_time_out);
    if (!newOut) { toast.error('Saída ajustada inválida (use HH:MM, ex: 22:30)'); return; }
    if (!form.justification.trim()) { toast.error('Justificativa é obrigatória'); return; }

    let original = normalizeTime(form.original_time_out);
    if (!original) {
      try {
        const r = await axios.get(`${API}/employees/${form.employee_id}/scheduled-end`, {
          params: { date: form.date }, withCredentials: true,
        });
        original = r.data?.scheduled_end || null;
      } catch (err) {
        devError('adj-resolve-original', err);
      }
    }
    if (!original) { toast.error('Sem horário agendado — preencha "Saída original"'); return; }

    const oMin = timeToMinutes(original);
    const nMin = timeToMinutes(newOut);
    const delta = nMin - oMin;
    if (delta === 0) { toast.error('O novo horário deve ser diferente do original'); return; }

    try {
      await axios.post(`${API}/time-adjustments`, {
        employee_id: form.employee_id,
        location_id: form.location_id,
        date: form.date,
        original_time_out: original,
        new_time_out: newOut,
        delta_minutes: delta,
        reason: delta > 0 ? 'overtime' : 'early_exit',
        justification: form.justification.trim(),
      }, { withCredentials: true });
      toast.success(`Ajuste registado (${delta > 0 ? '+' : ''}${delta} min)`);
      setDialogOpen(false);
      setForm({
        employee_id: '', location_id: '', date: format(new Date(), 'yyyy-MM-dd'),
        original_time_out: '', new_time_out: '', reason: 'overtime', justification: ''
      });
      fetchAdjustments();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erro ao salvar ajuste';
      toast.error(msg);
    }
  };

  const revokeAdjustment = async () => {
    if (!confirmRevoke) return;
    try {
      await axios.delete(`${API}/time-adjustments/${confirmRevoke.adjustment_id}`, { withCredentials: true });
      toast.success('Ajuste revogado (registado em log)');
      setConfirmRevoke(null);
      fetchAdjustments();
    } catch (err) {
      toast.error('Erro ao revogar');
    }
  };

  const formatMin = (min) => {
    const sign = min > 0 ? '+' : '';
    if (Math.abs(min) >= 60) {
      const h = Math.floor(Math.abs(min) / 60);
      const m = Math.abs(min) % 60;
      return `${sign}${min < 0 ? '-' : ''}${h}h${m > 0 ? `${m}min` : ''}`;
    }
    return `${sign}${min}min`;
  };

  const formatDt = (iso) => {
    try { return format(new Date(iso), "dd/MM/yyyy HH:mm"); }
    catch { return iso; }
  };

  return (
    <AdminLayout title="Ajustes de Horário">
      <div className="mb-6 max-w-3xl">
        <div className="flex items-start gap-3 p-4 bg-[#FEF3C7] border border-[#F59E0B]">
          <Shield className="w-5 h-5 text-[#92400E] flex-shrink-0 mt-0.5" />
          <div className="text-sm text-[#92400E]">
            <strong>Auditoria de Banco de Horas.</strong> Apenas ajustes registados aqui contam para o
            banco de horas. Cada ajuste exige justificativa e fica gravado com gerente, data e hora.
            Atrasos no início do turno NÃO geram ajuste — são tratados como atraso.
          </div>
        </div>
      </div>

      {/* Filters + Action */}
      <div className="bg-white border border-[#E5E7EB] p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider mb-2 block">Funcionário</Label>
            <Select value={filterEmployee} onValueChange={setFilterEmployee}>
              <SelectTrigger data-testid="adj-filter-employee"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {employees.map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider mb-2 block">Local</Label>
            <Select value={filterLocation} onValueChange={setFilterLocation}>
              <SelectTrigger data-testid="adj-filter-location"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {locations.map(l => <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider mb-2 block">Tipo</Label>
            <Select value={filterReason} onValueChange={setFilterReason}>
              <SelectTrigger data-testid="adj-filter-reason"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="overtime">Hora Extra</SelectItem>
                <SelectItem value="early_exit">Saída Antecipada</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider mb-2 block">De</Label>
            <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} data-testid="adj-filter-start-date" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider mb-2 block">Até</Label>
            <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} data-testid="adj-filter-end-date" />
          </div>
        </div>
        <div className="flex flex-wrap gap-3 mt-4">
          <Button onClick={fetchAdjustments} disabled={loading} data-testid="adj-refresh-btn" className="bg-[#111111] text-white hover:bg-[#1F1F1F]">
            <Search className="w-4 h-4 mr-2" />{loading ? 'Buscando...' : 'Atualizar'}
          </Button>
          <Button onClick={() => setDialogOpen(true)} data-testid="adj-add-btn" className="bg-[#0029FF] hover:bg-[#0022CC]">
            <Plus className="w-4 h-4 mr-2" />Registar Ajuste
          </Button>
          <span className="text-sm text-[#6B7280] self-center" data-testid="adj-count">
            {items.length} ajuste(s)
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="border border-[#E5E7EB] overflow-x-auto bg-white">
        <table className="data-table">
          <thead>
            <tr>
              <th>Data</th>
              <th>Funcionário</th>
              <th>Local</th>
              <th>Tipo</th>
              <th>Saída original</th>
              <th>Saída ajustada</th>
              <th>Delta</th>
              <th>Justificativa</th>
              <th>Aprovado por</th>
              <th>Aprovado em</th>
              <th>Ação</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={11} className="text-center py-8"><div className="w-6 h-6 border-2 border-[#111111] border-t-transparent rounded-full animate-spin mx-auto" /></td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={11} className="text-center py-8 text-[#6B7280]" data-testid="adj-empty">Nenhum ajuste registado.</td></tr>
            ) : items.map(a => (
              <tr key={a.adjustment_id} data-testid={`adj-row-${a.adjustment_id}`}>
                <td className="mono">{a.date}</td>
                <td className="font-medium">{a.employee_name}</td>
                <td className="text-[#6B7280]">{a.location_name}</td>
                <td>
                  <span className={`badge ${a.reason === 'overtime' ? 'bg-[#059669]' : 'bg-[#F59E0B]'} text-white`}>
                    {a.reason === 'overtime' ? <ArrowUp className="w-3 h-3 inline mr-1" /> : <ArrowDown className="w-3 h-3 inline mr-1" />}
                    {REASON_LABELS[a.reason]}
                  </span>
                </td>
                <td className="mono">{a.original_time_out || '-'}</td>
                <td className="mono">{a.new_time_out}</td>
                <td className={`mono font-bold ${a.delta_minutes > 0 ? 'text-[#059669]' : 'text-[#DC2626]'}`}>
                  {formatMin(a.delta_minutes)}
                </td>
                <td className="text-sm max-w-xs truncate" title={a.justification}>{a.justification}</td>
                <td className="text-xs">
                  <div>{a.approved_by_name}</div>
                  <div className="text-[#9CA3AF]">{a.approved_by_email}</div>
                </td>
                <td className="text-xs text-[#6B7280] mono">{formatDt(a.approved_at)}</td>
                <td>
                  <Button variant="ghost" size="icon" onClick={() => setConfirmRevoke(a)} className="text-[#DC2626]" data-testid={`adj-revoke-${a.adjustment_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* New Adjustment Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-black" style={{ fontFamily: 'Satoshi, sans-serif' }}>
              Registar Ajuste de Horário
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Funcionário *</Label>
                <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                  <SelectTrigger data-testid="adj-form-employee"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {employees.map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{e.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Local *</Label>
                <Select value={form.location_id} onValueChange={(v) => setForm({ ...form, location_id: v })}>
                  <SelectTrigger data-testid="adj-form-location"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {locations.map(l => <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Data *</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="adj-form-date" />
              </div>
              <div>
                <Label>Saída Original</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  placeholder="HH:MM (ex: 18:00)"
                  value={form.original_time_out}
                  onChange={(e) => setForm({ ...form, original_time_out: e.target.value })}
                  onBlur={(e) => {
                    const norm = normalizeTime(e.target.value);
                    if (norm) setForm((prev) => ({ ...prev, original_time_out: norm }));
                  }}
                  data-testid="adj-form-original"
                />
              </div>
              <div>
                <Label>Saída Ajustada *</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  placeholder="HH:MM (ex: 22:30)"
                  value={form.new_time_out}
                  onChange={(e) => setForm({ ...form, new_time_out: e.target.value })}
                  onBlur={(e) => {
                    const norm = normalizeTime(e.target.value);
                    if (norm) setForm((prev) => ({ ...prev, new_time_out: norm }));
                  }}
                  data-testid="adj-form-new"
                />
              </div>
            </div>
            <div className="bg-[#F3F4F6] p-3 text-sm">
              <strong>Delta:</strong>{' '}
              <span className={`mono font-bold ${computeDeltaMinutes() > 0 ? 'text-[#059669]' : computeDeltaMinutes() < 0 ? 'text-[#DC2626]' : 'text-[#6B7280]'}`} data-testid="adj-form-delta">
                {computeDeltaMinutes() === null ? '— preencha os horários (HH:MM)' : computeDeltaMinutes() === 0 ? '0min' : formatMin(computeDeltaMinutes())}
              </span>
              {computeDeltaMinutes() !== null && computeDeltaMinutes() !== 0 && (
                <span className="text-[#6B7280] ml-2">
                  ({computeDeltaMinutes() > 0 ? 'Hora Extra' : 'Saída Antecipada'})
                </span>
              )}
            </div>
            <div>
              <Label>Justificativa Expressa *</Label>
              <Textarea
                rows={3}
                value={form.justification}
                onChange={(e) => setForm({ ...form, justification: e.target.value })}
                placeholder="Ex: Cobertura de evento, saída autorizada para consulta médica..."
                data-testid="adj-form-justification"
              />
              <p className="text-[10px] text-[#6B7280] mt-1">
                Esta justificativa fica registada permanentemente e é acessível ao admin.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={submitAdjustment} data-testid="adj-form-submit" className="bg-[#0029FF] hover:bg-[#0022CC]">Salvar Ajuste</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke confirm */}
      <AlertDialog open={!!confirmRevoke} onOpenChange={(o) => !o && setConfirmRevoke(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revogar este ajuste?</AlertDialogTitle>
            <AlertDialogDescription>
              O ajuste de <strong className="mono">{formatMin(confirmRevoke?.delta_minutes || 0)}</strong> de{' '}
              <strong>{confirmRevoke?.employee_name}</strong> em {confirmRevoke?.date} será apagado e
              o banco de horas será recalculado. A revogação fica registada em log com seu nome.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={revokeAdjustment} className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="adj-confirm-revoke">
              Revogar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
